<template>
    <div class="patient-form-page" :class="{ 'patient-form-page--postop': isPostOp }">
        <!-- Dead/expired/deleted link → friendly "contact the office" page, never a broken form -->
        <LinkNotFound v-if="linkNotFound" :inactive="siteInactive" />
        <template v-else>
        <!-- Patient-facing Header with Logo -->
        <div class="patient-form-header" v-if="isPostOp || logoUrl || (formName && !(formData && formData.formConfig && formData.formConfig.isShowHeadline))">
            <div class="header-content">
                <img v-if="logoUrl" :src="logoUrl" alt="Logo" class="practice-logo" />
                <!-- Post-op after-care link: the PRACTICE and what this is. Never the patient. -->
                <div v-if="isPostOp && siteName" class="postop-practice" data-test="post-op-practice">{{ siteName }}</div>
                <div v-if="isPostOp" class="postop-eyebrow" data-test="post-op-eyebrow">{{ postOpText('postOpEyebrow') }}</div>
                <h1 v-if="formName && !(formData && formData.formConfig && formData.formConfig.isShowHeadline)" class="form-title">{{ formName }}</h1>
                <!-- Language Toggle for multi-language forms -->
                <LanguageToggle
                    v-if="showLanguageToggle"
                    :form-config="formData"
                    :initial-language="currentLanguage"
                    @language-changed="handleLanguageChange"
                    class="header-language-toggle"
                />
            </div>
        </div>

        <!-- Packet progress: "<Packet name> · Section 3 of 8" + bar. Renders only when this form
             arrived with packet numbers in the query; a standalone form is byte-identical. -->
        <PacketProgressStrip
            v-if="packetProgress && packetProgress.total"
            :packet-name="packetProgress.packetName"
            :step="packetProgress.step"
            :total="packetProgress.total"
            :language="currentLanguage"
        />

        <ErrorDisplay />

        <!-- Form Container -->
        <div class="patient-form-container">
            <FormRenderer v-if="formData" v-model="formInputData"
                :form-configuration="formData"
                :read-only="readOnly" :siteID="siteID" :patNum="patNum" :logoUrl="logoUrl"
                :formSubmissionIDForWeb="formSubmissionIDForWeb"
                :packet-progress="packetProgress"
                :paging-enabled="true"
                :draft-enabled="draftEnabled"
                :initial-part="draftPartIndex"
                @save-draft="saveDraft"
                :language="currentLanguage" />
            <!-- Loading State - show while form is loading -->
            <div v-else class="loading-state">
                <i class="fa fa-spinner fa-spin fa-2x"></i>
                <span>Loading form...</span>
            </div>
        </div>

        <!-- Post-op after-care link only (this ViewForm's OWN getLongUrl said linkMode 'postop'): the sticky "I've read these instructions" bar. -->
        <PostOpAckBar v-if="isPostOp && formData" :url-id="urlID || formUrlID" :language="currentLanguage" />

        <!-- Custom form submission overlay -->
        <FormSubmissionOverlay
            :show="busy"
            title="Submitting Your Form"
            message="Please wait while we process your submission. This may take a few moments..."
        />

        <!-- Debug toggle button - only visible when debug=1 is in URL -->
        <button v-if="isDebugMode" @click="toggleMobileLogger" class="debug-toggle-btn">
            Toggle Debug Log
        </button>

        <PoweredByFooter />
        </template>
    </div>
</template>

<script>
import Vue from 'vue';
import FormBuilder from "./FormBuilder";
import FormRenderer from "./FormRenderer";
import LanguageToggle from "./LanguageToggle.vue";
import { mapState, mapActions, mapMutations } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import PoweredByFooter from '@/components/PoweredByFooter.vue';
import LinkNotFound from '@/components/LinkNotFound/LinkNotFound.vue';
import { isDeadLinkResponse } from '@/utils/deadLink';
import { isSiteInactiveResponse } from '@/utils/siteInactiveLink';
import FormSubmissionOverlay from "./FormSubmissionOverlay";
import Multiselect from "vue-multiselect";
import _ from 'lodash';
import LogRocket from 'logrocket';
import moment from 'moment-timezone';
import { getLanguagePreference, setLanguagePreference, formSupportsLanguage, SUPPORTED_LANGUAGES } from '../utils/languageHelper';
import { sanitizeFormConfig } from '../libraries/sanitize-config';
import { collectFormAttachments } from '../libraries/collect-attachments';
import { resolvePacketProgress, nextPacketQuery } from '../libraries/packet-progress';
import { t } from '../utils/systemTranslations';
import { isCategorizedDiseases, getOtherNote, OTHER_NOTE_KEY } from '../libraries/od-categorized-diseases';
import PacketProgressStrip from './PacketProgressStrip.vue';
import PostOpAckBar from './PostOpAckBar.vue';
import { stripSideEffectControls } from '../libraries/post-op-display';
import { CONTROLS } from '../configs/controls';
import { VALIDATION_RULES } from '../configs/validation';

// Global log buffer
const globalLogBuffer = [];

import "../css/bootstrap-4.3.1.min.css";
import "../css/v-form-builder.css";

// Import the mobile logger
import { mobileLogger } from '../../../utils/mobileLogger';

export default {
    name: "ViewForm",
    props: ['formUrlID', 'urlID', 'previewFormID'],
    components: {
        FormBuilder,
        FormRenderer,
        LanguageToggle,
        ErrorDisplay,
        Multiselect,
        FormSubmissionOverlay,
        PoweredByFooter,
        LinkNotFound,
        PacketProgressStrip,
        PostOpAckBar
    },

    beforeDestroy() {
        this.$formEvent.$off("submit", this._submitHandler);

        // Restore original console methods
        if (this._originalConsole) {
            ['log', 'info', 'warn', 'error', 'dir', 'debug'].forEach(level => {
                if (this._originalConsole[level]) {
                    console[level] = this._originalConsole[level];
                }
            });
        }

        // Clear the global buffer when leaving the page
        globalLogBuffer.length = 0;

        // Only cleanup mobile logger if it was initialized
        if (this.isDebugMode) {
            const logViewer = document.getElementById("mobileLog");
            if (logViewer) {
                logViewer.parentElement?.removeChild(logViewer);
                const formContainer = document.querySelector('.formpage');
                if (formContainer) formContainer.style.paddingRight = "0";
            }
        }
    },

    async created() {
        // Initialize logger only when debug=1 is in URL
        if (this.$route.query.debug === '1') {
            // Clean up any existing logger first
            const existingLogger = document.getElementById("mobileLog");
            if (existingLogger) {
                existingLogger.parentElement?.removeChild(existingLogger);
            }

            // Initialize logger
            window.$mlog = mobileLogger;
            mobileLogger.log(`ViewForm created with formUrlID: ${this.formUrlID}`);

            // Show logger and adjust padding
            Vue.nextTick(() => {
                mobileLogger.show();
                const formContainer = document.querySelector('.formpage');
                if (formContainer) formContainer.style.paddingRight = "300px";
            });
        }

        // --- Console log capture setup ---
        const levels = ['log', 'info', 'warn', 'error', 'dir', 'debug'];
        this._originalConsole = {};
        levels.forEach(level => {
            this._originalConsole[level] = console[level];
            console[level] = (...args) => {
                try {
                    globalLogBuffer.push({
                        level,
                        timestamp: new Date().toISOString(),
                        args: args.map(a => {
                            try { return JSON.stringify(a); } catch { return String(a); }
                        })
                    });
                } catch (e) {
                    // Intentionally ignore errors in log capture
                }
                this._originalConsole[level](...args);
            };
        });

        await this.init();
        this._submitHandler = async (value) => {
            // A post-op after-care page NEVER saves anything: no submit button is drawn, and should a form still carry a Button control
            // (ButtonControl emits the same validation -> 'submit' chain) the event is ignored here. Plan 2026-10-15-post-op-auto-send §5c.
            if (this.isPostOp) {
                this.$formEvent.$emit('submitComplete');
                return;
            }
            try {
                if (!this.formIDForWeb) {
                    this.$toast.error('Somethng is not right. Please try refreshing and submitting the form again');
                    this.logToMobile('Error: formIDForWeb is missing');
                    return;
                }

                if (!this.siteID) {
                    this.$toast.error('SiteID is missing. Please try refreshing and submitting the form again');
                    this.logToMobile('Error: siteID is missing');
                    return;
                }

                // Prevent duplicate submissions (race condition with packet redirects)
                if (this.submissionInProgress) return;
                this.submissionInProgress = true;

                this.logToMobile('Got formIDForWeb and siteID. Now trying to _saveFormSubmission');
                this.logToMobile('Form submission value received:', JSON.stringify(value, null, 2));
                LogRocket.log('Got formIDForWeb and siteID. Now trying to _saveFormSubmission');

                // Show the submission overlay
                this.busy = true;

                // Scroll to top to ensure overlay is visible
                window.scrollTo(0, 0);

                // Saved progress: let any in-flight draft save (Save & Continue on the part before)
                // land FIRST. The final submit deletes the draft server-side; a draft upsert that
                // arrived after that DELETE would resurrect it and re-prefill the next visit. The
                // chain never rejects (saveDraft catches), so this only waits.
                if (this.draftSaveChain) await this.draftSaveChain;

                await this._saveFormSubmission();

            } catch (error) {
                this.logToMobile('Error in form submission handler:', error);
                console.error('Form submission error:', error);
                this.$toast.error('An error occurred during form submission. Please try again.');
            } finally {
                this.busy = false;
                // Notify FormRenderer to reset submit button state
                this.$formEvent.$emit('submitComplete');
            }
        };
        this.$formEvent.$on("submit", this._submitHandler)
    },

    data: () => ({
        busy: false,
        linkNotFound: false,  // true → render <LinkNotFound/> instead of the form (410/404/403 from getLongUrl)
        siteInactive: false,  // with linkNotFound: the practice has left Teamio (200 'site-inactive') → inactive copy
        formIDForWeb: null,
        formSubmissionIDForWeb: null,
        formSubmissionData: {},
        patientOdData: [],
        patientLName: null,  // For pre-fill patient name feature (from getLongUrl)
        patientFName: null,  // For pre-fill patient name feature (from getLongUrl)
        teethNumbers: null,  // For pre-fill teeth numbers feature (from getLongUrl)
        siteName: null,      // Practice/office name for dynamic placeholder replacement
        sitePhone: null,     // Office phone for dynamic placeholder replacement
        patientOdNoteData: [],
        patientOdAllergies: [],
        patientOtherAllergies: [],
        patientOdDiseases: [],
        patientOtherProblems: [],
        patientOdMedications: [],
        patientOtherMedications: [],
        patientOdReferrals: [],   // res2.data[6] — the patient's existing OD referral sources (RefType 1)
        siteID: null,
        patNum: null,
        forms: [],
        formOptions: [],
        selectedForm: [],
        formName: "",
        formType: "",
        formData: null,
        isShowDevNote: false,
        isRenderer: false,
        formInputData: null,
        isShowData: false,
        readOnly: false,
        // true ONLY when this ViewForm's own getLongUrl returned linkMode === 'postop' (a post-op after-care link): rendered WITHOUT submit, no prefill, no patient details, nothing saved.
        isPostOp: false,
        logoUrl: null,
        _originalConsole: {},
        currentLanguage: 'en',
        submissionInProgress: false,
        // Saved progress for section break points (libraries/form-parts.js). Patient-specific
        // links only: a generic / kiosk link has no PatNum and never persists anything.
        draftEnabled: false,
        draftPartIndex: 0,   // 0-based part a resumed draft lands on (FormRenderer initial-part)
        pendingDraft: null,  // getLongUrl's `draft` ({ data, partIndex, updatedOn }) until overlaid
    }),

    methods: {
        ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
        ...mapActions('forms', ['getForm', 'getForms', 'saveForm', 'saveFormSubmission', 'getFormSubmissionFromFormIDForWeb', 'captureFormLogs']),
        ...mapActions('urlredirect', ['getLongUrl']),

        logToMobile(message, data) {
            if (this.isDebugMode && window.$mlog) {
                if (data) {
                    mobileLogger.log(message, data);
                } else {
                    mobileLogger.log(message);
                }
            }
        },

        handleLanguageChange(newLanguage) {
            this.currentLanguage = newLanguage;
            setLanguagePreference(newLanguage);
            this.logToMobile(`Language changed to: ${newLanguage}`);
        },

        /**
         * Replace dynamic placeholders in wiki controls with actual site info.
         * Supports: [PRACTICE/OFFICE NAME], [OFFICE PHONE NUMBER]
         * Called after formData is loaded and site info is available.
         */
        replaceSitePlaceholders() {
            if (!this.formData || !this.formData.controls) return;
            if (!this.siteName && !this.sitePhone) return;

            for (const controlId of Object.keys(this.formData.controls)) {
                const control = this.formData.controls[controlId];
                
                // Handle wiki controls (Editor.js JSON in wikiContent)
                if (control.type === 'wiki' && control.wikiContent) {
                    let updated = control.wikiContent;
                    if (this.siteName) {
                        updated = updated.replace(/\[PRACTICE\/OFFICE NAME\]/g, this.siteName);
                    }
                    if (this.sitePhone) {
                        updated = updated.replace(/\[OFFICE PHONE NUMBER\]/g, this.sitePhone);
                    }
                    if (updated !== control.wikiContent) {
                        control.wikiContent = updated;
                        this.logToMobile(`Replaced site placeholders in wiki control: ${controlId}`);
                    }
                }

                // Handle html controls (raw HTML in defaultValue)
                if (control.type === 'html' && control.defaultValue) {
                    let updated = control.defaultValue;
                    if (this.siteName) {
                        updated = updated.replace(/\[PRACTICE\/OFFICE NAME\]/g, this.siteName);
                    }
                    if (this.sitePhone) {
                        updated = updated.replace(/\[OFFICE PHONE NUMBER\]/g, this.sitePhone);
                    }
                    if (updated !== control.defaultValue) {
                        control.defaultValue = updated;
                        this.logToMobile(`Replaced site placeholders in html control: ${controlId}`);
                    }
                }
            }
        },

        validateAndFixFormControls() {
            try {
                if (!this.formData || !this.formData.controls) {
                    return;
                }

                // ⛔ THE 2026-02-18 SILENT-STRIP BUG (commit f7709953), fixed 2026-09-13.
                // This list used to be hardcoded in camelCase ('isEmail', 'sameAs',
                // 'customClosure') while the check below lowercases rule.ruleType — so
                // every isEmail / sameAs / customClosure rule was DELETED from the form on
                // every patient load, with nothing but a console.warn. isEmail was dead on
                // patient forms for seven months and a garbage email submitted fine. Any
                // new rule type would have died the same way on arrival.
                // Derive it from the registry instead: one source of truth, and a rule
                // added to configs/validation.js is supported here the moment it exists.
                const supportedRuleTypes = Object.keys(VALIDATION_RULES).map(k => k.toLowerCase());
                let fixedCount = 0;

                for (const key of Object.keys(this.formData.controls)) {
                    const control = this.formData.controls[key];

                    // The real validation system uses control.validations[] with rule.ruleType
                    if (control.validations && Array.isArray(control.validations)) {
                        const validRules = control.validations.filter(rule => {
                            if (!rule || typeof rule !== 'object') {
                                fixedCount++;
                                return false;
                            }
                            if (!rule.ruleType || rule.ruleType === '') {
                                fixedCount++;
                                return false;
                            }
                            // Case-insensitive check to catch REQUIRED, Required, etc.
                            if (!supportedRuleTypes.includes(rule.ruleType.toLowerCase())) {
                                console.warn(`[validateAndFixFormControls] Removing unsupported ruleType: ${rule.ruleType} on control ${control.name || key}`);
                                fixedCount++;
                                return false;
                            }
                            return true;
                        });
                        control.validations = validRules;
                    }
                }

                if (fixedCount > 0) {
                    console.warn(`[validateAndFixFormControls] Fixed ${fixedCount} validation rule issues`);
                }
            } catch (error) {
                console.error('[validateAndFixFormControls] Error:', error);
            }
        },

        toggleMobileLogger() {
            const logViewer = document.getElementById("mobileLog");
            if (!logViewer) return;

            if (logViewer.style.display === 'none') {
                logViewer.style.display = 'block';
                const formContainer = document.querySelector('.formpage');
                if (formContainer) formContainer.style.paddingRight = "300px";
            } else {
                logViewer.style.display = 'none';
                const formContainer = document.querySelector('.formpage');
                if (formContainer) formContainer.style.paddingRight = "0";
            }
        },

        async init() {
            let res, res2, res3;
            let submissionFound = false;

            // Language will be set after we get patient data from getLongUrl
            // For now, initialize from URL param or preference as fallback
            let initialLanguage = getLanguagePreference();
            this.logToMobile(`Initial language from URL/localStorage/browser: ${initialLanguage}`);

            // DEBUG: Log language detection details
            console.log('[ViewForm DEBUG] Initial language detection (before patient data):', {
                initialLanguage: initialLanguage,
                urlLangParam: new URLSearchParams(window.location.search).get('lang'),
                fullUrl: window.location.href
            });

            console.log('ViewForm init() called with props:', {
                formUrlID: this.formUrlID,
                urlID: this.urlID,
                previewFormID: this.previewFormID
            });

            // Check if this is preview mode (direct formIDForWeb access)
            if (this.previewFormID) {
                console.log('Preview mode: Loading form directly by formIDForWeb:', this.previewFormID);
                this.logToMobile(`Preview mode: Loading form ${this.previewFormID}`);

                // Preview mode: Use URL param or localStorage/browser preference (no patient data available)
                this.currentLanguage = initialLanguage;
                this.logToMobile(`Preview mode language set to: ${this.currentLanguage}`);

                // Get siteID from localStorage for preview mode
                this.siteID = window.localStorage.teamioSiteID;
                this.formIDForWeb = this.previewFormID;

                res3 = await this.getForm({ formIDForWeb: this.previewFormID });
                this.logToMobile('Preview form results', res3);

                if (res3.data) {
                    const { sanitized: previewConfig } = sanitizeFormConfig(JSON.parse(res3.data.config.ConfigData), { controlDefinitions: CONTROLS });
                    this.formData = Object.assign({}, previewConfig, { showSubmit: false }); // No submit in preview
                    this.replaceSitePlaceholders();
                    this.formName = res3.data.config.Name;
                    this.formType = res3.data.config.FormType;
                    this.logoUrl = res3.data.config.LogoUrl || null;

                    // DEBUG: Check translation support
                    console.log('[ViewForm] Form loaded:', {
                        formName: this.formName,
                        currentLanguage: this.currentLanguage,
                        supportedLanguages: this.formData?.formConfig?.supportedLanguages
                    });

                    // Validate form controls
                    this.validateAndFixFormControls();

                    // Set up form input data
                    this.formInputData = {};

                    this.logToMobile(`Preview form loaded: ${this.formName}`);
                } else {
                    console.error('Failed to load form for preview');
                    this.logToMobile('ERROR: Failed to load form for preview');
                }
                return; // Exit early for preview mode
            }

            const idToUse = this.urlID || this.formUrlID;
            console.log('Using ID for getLongUrl:', idToUse);

            if (!idToUse) {
                console.error('ViewForm: No valid ID available for getLongUrl');
                this.logToMobile('ERROR: No valid ID available for getLongUrl');
                return;
            }

            // THIS is the call whose `teethNumbers` the form actually renders (see the
            // assignment below and the prefill loop further down) — NOT the earlier
            // getLongUrl in UrlRedirect.vue, whose response is used only to decide
            // routing and is then discarded.
            //
            // aptNum/codes carry the procedure scope from a Get Form QR code so the
            // "Teeth #s" field prefills with only that consent's teeth. They reach us
            // through the route query: UrlRedirect spreads `{ ...incomingQuery }` into
            // every push and VerifyForm forwards it again — the same mechanism that
            // delivers ?txPlan (read below in _saveFormSubmission). Omitting them here
            // silently returns the UNSCOPED teeth for the whole appointment.
            // txPlan is set by Tx Presenter's consent wizard
            // (PatientNextStepsWizard.appendTxPlan); aptNum/codes by the Get Form QR
            // modal. Both scope the "Teeth #s" prefill to just this consent's
            // procedures — the backend picks a branch and falls back when absent.
            res = await this.getLongUrl({
                urlID: idToUse,
                aptNum: (this.$route.query || {}).aptNum,
                codes: (this.$route.query || {}).codes,
                txPlan: (this.$route.query || {}).txPlan,
            });
            this.logToMobile('getLongUrl response', res);

            // Dead/expired/deleted link → show the friendly "contact the office" page.
            // MUST run before `if (res.data)`: a 410 body is the truthy string
            // "URL Not Found...", so a data-first check would render a broken empty form.
            if (isDeadLinkResponse(res)) {
                this.logToMobile(`Link not found (status ${res.status}) — showing LinkNotFound`);
                this.linkNotFound = true;
                return;
            }

            // The practice behind this link has left Teamio (200 'site-inactive'). MUST run before `if (res.data)`,
            // which would read the body as a form and render it empty.
            if (isSiteInactiveResponse(res)) {
                this.logToMobile('Practice no longer on Teamio — showing LinkNotFound (inactive)');
                this.siteInactive = true;
                this.linkNotFound = true;
                return;
            }

            if (res.data) {
                this.formName = res.data.formName;
                this.formIDForWeb = res.data.formIDForWeb;
                this.formType = res.data.formType;
                this.siteID = res.data.siteID;
                this.logoUrl = res.data.logoUrl;

                // CRITICAL (Jan 21, 2026 fix): Set language from patient's OD preference
                // Priority: 1) URL param (?lang=es), 2) Patient's OD language preference, 3) localStorage, 4) browser, 5) default 'en'
                const urlLangParam = new URLSearchParams(window.location.search).get('lang');
                if (urlLangParam && SUPPORTED_LANGUAGES.includes(urlLangParam)) {
                    // URL param has highest priority - explicit override
                    this.currentLanguage = urlLangParam;
                    this.logToMobile(`Language set from URL param: ${this.currentLanguage}`);
                } else if (res.data.patientLanguage) {
                    // Patient's OD language preference (from database)
                    // Open Dental uses ISO 639-2 codes: 'spa'=Spanish, 'rus'=Russian, etc.
                    const odLang = res.data.patientLanguage.toLowerCase();
                    const odLangMap = { spa: 'es', rus: 'ru' };
                    const mapped = odLangMap[odLang]
                        || SUPPORTED_LANGUAGES.find(l => l !== 'en' && odLang.startsWith(l));
                    if (mapped) {
                        this.currentLanguage = mapped;
                        this.logToMobile(`Language set from patient OD preference: ${this.currentLanguage} (raw: ${res.data.patientLanguage})`);
                    } else {
                        this.currentLanguage = 'en';
                        this.logToMobile(`Language set to English (patient OD preference: ${res.data.patientLanguage})`);
                    }
                } else {
                    // Fallback to localStorage/browser preference
                    this.currentLanguage = initialLanguage;
                    this.logToMobile(`Language set from localStorage/browser fallback: ${this.currentLanguage}`);
                }

                console.log('[ViewForm DEBUG] Final language selection:', {
                    currentLanguage: this.currentLanguage,
                    urlLangParam: urlLangParam,
                    patientLanguage: res.data.patientLanguage
                });

                this.logToMobile(`Form data loaded: ${this.formName}, type: ${this.formType}`);

                // Store site info for dynamic placeholder replacement in wiki controls
                if (res.data.siteName) {
                    this.siteName = res.data.siteName;
                    this.sitePhone = res.data.sitePhone;
                    this.logToMobile(`Site info from getLongUrl: ${this.siteName}, ${this.sitePhone}`);
                }

                // POST-OP AFTER-CARE LINK (plan 2026-10-15-post-op-auto-send §5c). The mode comes from THIS call's linkMode — the link row's own
                // value — never from a query-string flag, and never from UrlRedirect's separate getLongUrl (that one only routes). The branch
                // returns BEFORE any patient field (name, teeth, patNum, OD prefill, draft) is stored: the page shows the practice and the
                // instructions, nothing about the patient.
                if (res.data.linkMode === 'postop') {
                    await this.initPostOp();
                    return;
                }

                // Store patient name for pre-fill feature (available for OTHER forms with prefillPatientName controls)
                if (res.data.patientLName) {
                    this.patientLName = res.data.patientLName;
                    this.patientFName = res.data.patientFName;
                    this.logToMobile(`Patient name from getLongUrl: ${this.patientLName}, ${this.patientFName}`);
                }

                // Store teeth numbers for pre-fill feature
                if (res.data.teethNumbers) {
                    this.teethNumbers = res.data.teethNumbers;
                    this.logToMobile(`Teeth numbers from getLongUrl: ${this.teethNumbers}`);
                }

                if (res.data.patNum !== null) {
                    this.patNum = res.data.patNum;
                    this.logToMobile(`Patient number: ${this.patNum}`);

                    // Saved progress (break points): patient-specific links only, never the
                    // builder preview (which returned above anyway). HANDSHAKE: only a forms-service
                    // that understands drafts sends the `draft` key (null or an object). An older one
                    // would treat `draft: true` as a FULL submission (row, PDF, Open Dental write-back),
                    // so without the key the button stays "Continue" and nothing is persisted.
                    this.draftEnabled = Number.isInteger(this.patNum) && this.patNum > 0 && !this.previewFormID && Object.prototype.hasOwnProperty.call(res.data, 'draft');
                    this.pendingDraft = this.draftEnabled ? (res.data.draft || null) : null;

                    if (this.formType !== 'OTHER') {
                        this.logToMobile(`Getting form submission for non-OTHER form type`);
                        // urlID = the SAME id getLongUrl just resolved (idToUse): the server only returns this patient's
                        // details to the link they belong to. Without it the call is refused (403).
                        res2 = await this.getFormSubmissionFromFormIDForWeb({ formIDForWeb: this.formIDForWeb, siteID: this.siteID, patNum: this.patNum, urlID: idToUse });
                        this.logToMobile(`Form submission response received.`, res2);
                        console.dir(res2);
                        if (res2 && res2.data) {
                            let submissionData;
                            if (res2.data[0].length > 0) {
                                submissionData = res2.data[0][0];
                                submissionFound = true;
                                this.logToMobile(`Previous submission found`);
                                const { sanitized: submissionConfig } = sanitizeFormConfig(JSON.parse(submissionData.ConfigData), { controlDefinitions: CONTROLS });
                                this.formData = Object.assign({}, submissionConfig, { showSubmit: true });
                                this.replaceSitePlaceholders();
                                this.logToMobile(`Config data loaded`);

                                this.formSubmissionData = Object.assign({}, JSON.parse(submissionData.FormSubmissionData));
                                this.formSubmissionIDForWeb = submissionData.FormSubmissionIDForWeb;

                                this.logToMobile(`Form submission ID: ${this.formSubmissionIDForWeb}`);
                                this.logToMobile('formInputData', this.formInputData);
                                this.logToMobile('formSubmissionData', this.formSubmissionData);
                            }

                            this.patientOdData = res2.data[1][0];
                            this.patientOdNoteData = res2.data[2][0];
                            this.patientOdAllergies = res2.data[3];
                            this.patientOtherAllergies = this.patientOdAllergies.filter(a => a.Description.trim().toUpperCase() === 'OTHER');
                            this.patientOdDiseases = res2.data[4];
                            this.patientOdMedications = res2.data[5];
                            // Index 6 was APPENDED to the prefill result sets, never inserted —
                            // everything above indexes positionally. Older backends won't send it.
                            this.patientOdReferrals = Array.isArray(res2.data[6]) ? res2.data[6] : [];
                        } else {
                            submissionFound = false;
                        }
                    }
                }

                if (!submissionFound) {
                    this.logToMobile(`submission not found. now getting form from formIDForWeb`);
                    res3 = await this.getForm({ formIDForWeb: this.formIDForWeb });
                    this.logToMobile('get form results', res3);

                    if (res3.data) {
                        const { sanitized: freshConfig } = sanitizeFormConfig(JSON.parse(res3.data.config.ConfigData), { controlDefinitions: CONTROLS });
                        this.formData = Object.assign({}, freshConfig, { showSubmit: true });
                        this.replaceSitePlaceholders();
                        this.formData.formConfig.pdfCategories = res.data.pdfCategories;
                    }
                    else {
                        this.logToMobile(`Something went wrong with getting this form: ${res3}`);
                    }
                }

                let _this = this;
                let signatureExists = false;
                let sectionUniqueIDWhereSignatureIs;
                let sectionUniqueIDWhereDateIs;

                this.logToMobile('this.patientOdData', this.patientOdData);

                // Validate form controls and fix any undefined validation types
                this.validateAndFixFormControls();

                // DEBUG: Check if controls loop is entered
                const controlKeys = Object.keys(this.formData.controls || {});
                console.log(`[DEBUG] About to iterate controls. Count: ${controlKeys.length}, patNum: ${this.patNum}`);

                for (const key of Object.keys(this.formData.controls)) {
                    let temp = this.formData.controls[key];

                    if (temp.teamioODColumn) {
                        this.logToMobile(`temp.teamioODColumn: ${temp.teamioODColumn}`);

                        // Referrals is keyed on the MAPPING, never on the control name: an office
                        // calls this "How did you hear about us?", so the name-substring matching
                        // used for allergies/problems/medications below could never find it.
                        if (temp.teamioODColumn === 'Referrals') {
                            this.convertReferralsForForm(temp.name, this.patientOdReferrals);
                        }

                        // DISEASES_CATEGORIZED is keyed on the MAPPING (like Referrals), never on the control
                        // name: the legacy converter below is triggered by the sibling "Other Problems" control's
                        // name and writes the literal key 'Diseases'; this control may be called anything.
                        if (temp.teamioODColumn === 'Diseases' && isCategorizedDiseases(temp)) {
                            this.convertCategorizedDiseasesForForm(temp.name, this.patientOdDiseases);
                        }

                        if (this.patientOdData.hasOwnProperty(temp.teamioODColumn)) {
                            if (temp.teamioODColumn === 'MedUrgNote') {
                                this.logToMobile('ignoring medurgnote for now');
                            } else {
                                this.logToMobile(`setting formSubmissionData['${temp.name}''] to ${this.patientOdData[temp.teamioODColumn]}`);
                                this.formSubmissionData[temp.name] = this.patientOdData[temp.teamioODColumn] + '';
                            }
                        }
                        else {
                            this.logToMobile('temp', temp);

                            if (temp.name && temp.name.toUpperCase().indexOf('ALLERGY') > 0)
                                this.convertAllergiesForForm(temp.name, this.patientOdAllergies);

                            if (temp.name && temp.name.toUpperCase().indexOf('PROBLEM') > 0)
                                this.convertDiseasesForForm(temp.name, this.patientOdDiseases);

                            if (temp.name && temp.name.toUpperCase().indexOf('MEDICATION') > 0)
                                this.convertMedicationsForForm(temp.name, this.patientOdMedications);
                        }
                    }

                    // Pre-fill Patient Name feature - for controls with prefillPatientName enabled
                    // Use patientLName/patientFName from getLongUrl (for OTHER forms) or fallback to patientOdData (for non-OTHER forms)
                    if (temp.prefillPatientName) {
                        const lName = this.patientLName || (this.patientOdData && this.patientOdData.LName);
                        const fName = this.patientFName || (this.patientOdData && this.patientOdData.FName);

                        if (lName && fName) {
                            const patientName = `${lName}, ${fName}`;
                            this.logToMobile(`Pre-filling patient name for control '${temp.name}': ${patientName}`);
                            this.formSubmissionData[temp.name] = patientName;
                            // Mark the control as readonly so FormRenderer can apply appropriate styling
                            temp.isPrefilled = true;
                        }
                    }

                    // Pre-fill Teeth Numbers feature - for controls with prefillTeethNumbers enabled
                    if (temp.prefillTeethNumbers) {
                        if (this.teethNumbers) {
                            this.logToMobile(`Pre-filling teeth numbers for control '${temp.name}': ${this.teethNumbers}`);
                            this.formSubmissionData[temp.name] = this.teethNumbers;
                            // NOTE: Do NOT set temp.isPrefilled = true — teeth numbers are editable
                        } else {
                            // No appointment found — mark control so InputControl shows a helper note
                            temp.teethNumbersEmpty = true;
                        }
                    }

                    // Pre-fill Today's Date feature - for date picker controls with prefillTodayDate enabled
                    // Works for all forms (patient-linked, share links, QR codes, walk-in tablets)
                    if (temp.prefillTodayDate) {
                        const today = new Date();
                        const month = String(today.getMonth() + 1).padStart(2, '0');
                        const day = String(today.getDate()).padStart(2, '0');
                        const year = today.getFullYear();
                        const todayFormatted = `${month}/${day}/${year}`;
                        // Use uniqueId as fallback when name is empty (date controls often have no name set)
                        const controlKey = temp.name || temp.uniqueId;
                        this.logToMobile(`Pre-filling today's date for control '${controlKey}': ${todayFormatted}`);
                        this.formSubmissionData[controlKey] = todayFormatted;
                        // Mark the control as readonly so FormRenderer can apply appropriate styling
                        temp.isPrefilled = true;
                    }

                    if (temp.type === 'signature') {
                        signatureExists = true;
                        let signatureControlUniqueID = temp.uniqueId;
                        if (Array.isArray(this.formSubmissionData[signatureControlUniqueID])) {
                            this.formSubmissionData[signatureControlUniqueID] = "";
                        }

                        for (let section in this.formData.sections) {
                            let temp = this.formData.sections[section].controls.filter(c => c === signatureControlUniqueID);
                            if (temp.length > 0) {
                                sectionUniqueIDWhereSignatureIs = section.uniqueId;
                            }
                        }
                    }

                    if (temp.type === 'date' && signatureExists && !temp.prefillTodayDate) {
                        let dateControlUniqueID = temp.uniqueId;
                        let dateControlName = temp.name;
                        for (let section in this.formData.sections) {
                            let temp = this.formData.sections[section].controls.filter(c => c === dateControlUniqueID);
                            if (temp.length > 0) {
                                sectionUniqueIDWhereDateIs = section.uniqueId;
                                if (sectionUniqueIDWhereDateIs === sectionUniqueIDWhereSignatureIs) {
                                    if (dateControlName === "")
                                        this.formSubmissionData[dateControlUniqueID] = "";
                                    else
                                        this.formSubmissionData[dateControlName] = "";
                                }
                            }
                        }
                    }
                }

                // Saved progress: overlay the patient's draft HERE — after the prefill loop above
                // (it writes formSubmissionData and would clobber an earlier overlay) and before
                // formInputData is handed to the renderer below. The draft wins over the Open
                // Dental prefill and over a reloaded previous submission.
                this.applyPendingDraft();

                Vue.nextTick(() => {
                    this.$set(_this, 'formInputData', this.formSubmissionData);
                });

            } else {
                this.logToMobile(`Something went wrong with getting this form: ${res}`);
            }
        },

        /**
         * Post-op after-care link: load the form and render it NORMALLY, with no prefill and no submit.
         *
         * ⛔ NOT `readOnly`. FormRenderer's read-only mode is the SUBMITTED-ANSWERS view (ControlView.vue): it prints a label for every control
         * and swaps each field for its saved value, so text blocks / wiki / images never render and the patient would see a list of control
         * names. The instructions ARE display controls; what keeps the page harmless is that nothing can be sent from it:
         *   showSubmit:false          no Submit / Sign button (break points are stripped too, so there is no Continue / Back either)
         *   patNum never set          draftEnabled stays false, saveDraft is a no-op
         *   _submitHandler            ignores 'submit' in post-op mode (a stray Button control cannot save a submission)
         *   fileUploader controls     dropped (picking a file would upload it)
         * patNum / patientFName / patientLName / teethNumbers / the OD prefill are deliberately never set; the only action is PostOpAckBar.
         */
        async initPostOp() {
            this.isPostOp = true;
            this.logToMobile('Post-op after-care link: no submit, no prefill');
            const res3 = await this.getForm({ formIDForWeb: this.formIDForWeb });
            if (res3 && res3.data) {
                const { sanitized: config } = sanitizeFormConfig(JSON.parse(res3.data.config.ConfigData), { controlDefinitions: CONTROLS });
                this.formData = Object.assign({}, stripSideEffectControls(config), { showSubmit: false });
                this.replaceSitePlaceholders();
                this.validateAndFixFormControls();
                this.formInputData = {};
            } else {
                this.logToMobile(`Something went wrong with getting this form: ${res3}`);
            }
        },

        postOpText(key) {
            return t(key, this.currentLanguage);
        },

        getControlFromTeamioODColumn(teamioODColumn) {
            return _.findKey(this.formData.controls, function (o) {
                return o.teamioODColumn === teamioODColumn;
            });
        },

        /**
         * A "DISEASES with Category and Checkbox Options" field asks for "Other" itself and keeps the
         * description on its "Other" item (libraries/od-categorized-diseases.js). The Diseases list
         * write stores that note only when it INSERTs or re-activates "Other"; for an "Other" that is
         * already active, forms-service updates the note ONLY from Other Problems, so the note is sent
         * as Other Problems too. (Until 2026-09-18 forms-service also RESOLVED "Other" whenever Other
         * Problems arrived blank; that branch is gone — see forms-service PROJECT_NOTES.) A legacy field
         * mapped to Other Problems (refused by the builder since 2026-09-18) keeps priority.
         * MIRRORED in ViewSubmission.vue (forms-od-categorized-other-note.test.js).
         *
         * @param {Object} odDataToSave - built by the submit loop; mutated in place
         */
        applyCategorizedOtherNote(odDataToSave) {
            if (this.getControlFromTeamioODColumn('Other Problems') || odDataToSave['Other Problems'] !== undefined) return;
            const key = _.findKey(this.formData.controls, c => !!c && c.teamioODColumn === 'Diseases' && isCategorizedDiseases(c));
            if (!key) return;
            const note = getOtherNote(this.formInputData[this.formData.controls[key].name]);
            if (note !== '') odDataToSave['Other Problems'] = note;
        },

        convertAllergiesForForm(column, allergies) {
            let temp = [];
            let otherFound = false;

            allergies.forEach(element => {
                // Skip inactive allergies (StatusIsActive=0 means deactivated)
                if (element.StatusIsActive == 0) return;

                let obj = {};
                obj['text'] = element.Description;
                obj['value'] = element.AllergyDefNum;
                temp.push(obj);

                if (element.Description && element.Description.toUpperCase() === 'OTHER') {
                    // Only set if Reaction has a valid value (not undefined/null/empty)
                    const reaction = element.Reaction;
                    this.formSubmissionData[column] = (reaction !== undefined && reaction !== null && reaction !== '') ? reaction : '';
                    otherFound = true;
                }
            });

            this.formSubmissionData['Allergies'] = temp;
            if (!otherFound) {
                this.logToMobile(`other not found in allergy so setting this.formSubmissionData[${column}] to empty string`);
                this.formSubmissionData[column] = '';
            }
        },

        convertDiseasesForForm(column, diseases) {
            let temp = [];
            let otherFound = false;

            diseases.forEach(element => {
                // Skip resolved/inactive diseases (ProbStatus=0 is active, 2 is resolved)
                if (element.ProbStatus != 0) return;

                let obj = {};
                obj['text'] = element.DiseaseName;
                obj['value'] = element.DiseaseDefNum;
                temp.push(obj);
                if (element.DiseaseName && element.DiseaseName.toUpperCase() === 'OTHER') {
                    // Only set if PatNote has a valid value (not undefined/null/empty)
                    const patNote = element.PatNote;
                    this.formSubmissionData[column] = (patNote !== undefined && patNote !== null && patNote !== '') ? patNote : '';
                    otherFound = true;
                }
            });

            this.formSubmissionData['Diseases'] = temp;

            if (!otherFound) {
                this.logToMobile(`other not found in diseases so setting this.formSubmissionData[${column}] to empty string`);
                this.formSubmissionData[column] = '';
            }
        },

        /**
         * Pre-fill a DISEASES_CATEGORIZED control from the patient's charted problems.
         *
         * Deliberately NOT convertDiseasesForForm: that one is triggered by the sibling "Other
         * Problems" control's NAME, writes the list to the literal key 'Diseases' and puts the OD
         * "Other" PatNote in the control it was called for. A categorized control can be called
         * anything, so this writes to the control's OWN name and does nothing else. Every item is
         * left untagged — DiseasesCategorizedField derives the category from the control's config,
         * and a problem that is in no category is preserved untouched so the form can never resolve
         * something the patient was never shown.
         *
         * The active "Other" row's PatNote (Open Dental's Other Problems) rides on its item as
         * `otherTypeDescriptionText`: the field shows it in the "Other" box, and submit sends it
         * back as Other Problems, which is what keeps forms-service from resolving "Other".
         *
         * @param {string} column - the control's `name` (the formSubmissionData key)
         * @param {Array} diseases - rows of { DiseaseDefNum, DiseaseName, ProbStatus, PatNote }
         */
        convertCategorizedDiseasesForForm(column, diseases) {
            const rows = Array.isArray(diseases) ? diseases : [];
            const list = rows
                // ProbStatus 0 is active, 2 is resolved. Loose compare: OD sends it as a string here.
                .filter(element => element && element.ProbStatus == 0)
                .map(element => {
                    const item = { text: element.DiseaseName, value: element.DiseaseDefNum };
                    const patNote = element.PatNote;
                    if (String(element.DiseaseName || '').trim().toUpperCase() === 'OTHER'
                        && typeof patNote === 'string' && patNote.trim() !== '') {
                        item[OTHER_NOTE_KEY] = patNote;
                    }
                    return item;
                });

            this.logToMobile(`pre-filling ${list.length} charted problem(s) for categorized control '${column}'`);
            this.formSubmissionData[column] = list;
        },

        convertMedicationsForForm(column, medications) {
            let temp = [];
            let otherFound = false;

            medications.forEach(element => {
                // Skip discontinued medications (active = DateStop is '0001-01-01' or '1901-01-01' or null)
                const dateStop = element.DateStop ? String(element.DateStop).substring(0, 10) : null;
                if (dateStop && dateStop !== '0001-01-01' && dateStop !== '1901-01-01') return;

                let obj = {};
                obj['text'] = element.MedName;
                obj['value'] = element.MedicationNum;
                temp.push(obj);
                if (element.MedName && element.MedName.toUpperCase() === 'OTHER') {
                    // Only set if PatNote has a valid value (not undefined/null/empty)
                    const patNote = element.PatNote;
                    this.formSubmissionData[column] = (patNote !== undefined && patNote !== null && patNote !== '') ? patNote : '';
                    otherFound = true;
                }
            });

            this.formSubmissionData['Medications'] = temp;

            if (!otherFound) {
                this.logToMobile(`other not found in medications so setting this.formSubmissionData[${column}] to empty string`);
                this.formSubmissionData[column] = '';
            }
        },

        /**
         * Pre-fill a REFERRALS control with the patient's existing Open Dental referral source.
         *
         * `referrals` are the patient's RefType 1 refattach rows ordered by ItemOrder, so the
         * FIRST row is the primary source. Stored as a ONE-ITEM array to match what the control
         * writes back — anything else and the multiselect renders blank on a returning patient.
         *
         * @param {string} column - the control's `name` (the formSubmissionData key)
         * @param {Array} referrals - rows of { RefAttachNum, ReferralNum, ItemOrder, ReferralName }
         */
        convertReferralsForForm(column, referrals) {
            const rows = Array.isArray(referrals) ? referrals : [];
            const row = rows[0];

            if (!row || row.ReferralNum === undefined || row.ReferralNum === null) {
                this.logToMobile(`no existing OD referral for this patient, leaving '${column}' empty`);
                this.formSubmissionData[column] = [];
                return;
            }

            this.logToMobile(`pre-filling referral for control '${column}': ${row.ReferralName} (${row.ReferralNum})`);
            this.formSubmissionData[column] = [{
                text: row.ReferralName,
                value: row.ReferralNum
            }];
        },

        formsCustomLabel({ Name, FormIDForWeb }) {
            return `${Name}`;
        },

        getData() {
            console.log(JSON.stringify(this.formData))
        },

        viewRenderer() {
            if (!this.isRenderer) {
                this.setData();
                this.isRenderer = true;
                return;
            }

            this.isRenderer = false;
        },

        async _getForms() {
            let res = await this.getForms({ siteID: this.siteID });

            if (res && res.data.length > 0) {
                this.forms = res.forms;
                this.formCategories = res.categories;
                this.formOptions = this.forms.map(({ Name }) => Name);
            }
        },

        loadForm() {
            this.formData = JSON.parse(this.selectedForm.ConfigData);
            this.formName = this.selectedForm.Name;
        },

        async _sendCapturedLogs() {
            // Helper function to sanitize log arguments
            const sanitizeLogArgs = (args) => {
                return args.map(arg => {
                    try {
                        // If it's already a string, just return it
                        if (typeof arg === 'string') return arg;
                        
                        // If it's an object, try to parse and clean it
                        const obj = typeof arg === 'string' ? JSON.parse(arg) : arg;
                        
                        // Filter out large form configuration data
                        if (obj && typeof obj === 'object') {
                            // For form data, only keep essential fields
                            if (obj.ConfigData || obj.FormSubmissionData) {
                                const filtered = { ...obj };
                                // Keep form name and type, but remove large configs
                                filtered.formName = obj.Name || obj.formName;
                                filtered.formType = obj.formType;
                                delete filtered.ConfigData;
                                delete filtered.FormSubmissionData;
                                return JSON.stringify(filtered);
                            }
                            
                            // For other objects, stringify with formatting
                            return JSON.stringify(obj, null, 2);
                        }
                        
                        return String(arg);
                    } catch (e) {
                        return String(arg);
                    }
                });
            };

            // Format log entries with context
            const formattedLogs = globalLogBuffer.map((log, index) => {
                const args = sanitizeLogArgs(log.args);
                return {
                    index: index + 1,
                    level: log.level.toUpperCase(),
                    timestamp: log.timestamp,
                    message: args.join(' '),
                    context: `[${index + 1}] [${log.timestamp}] [${log.level.toUpperCase()}] ${args.join(' ')}`
                };
            });

            // Prepare cleaned log payload
            const logPayload = {
                logs: formattedLogs,
                platform: {
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    platform: navigator.platform,
                    screen: {
                        width: window.screen.width,
                        height: window.screen.height
                    },
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                }
            };

            try {
                // Log a summary to console for debugging
                console.log('Sending logs to server:', formattedLogs.map(log => log.context).join('\n'));
                
                await this.$store.dispatch('forms/captureFormLogs', logPayload);
                globalLogBuffer.length = 0;
            } catch (err) {
                globalLogBuffer.push({
                    level: 'error',
                    timestamp: new Date().toISOString(),
                    args: ['Failed to send logs', err && err.message ? err.message : String(err)]
                });
            }
        },

        /**
         * Save & Continue on a mid-form part (FormRenderer `save-draft`). The renderer has already
         * moved to the next part (optimistic); this persists the answers so far as a DRAFT through
         * the existing save-form-submission route (`draft: true` -> forms-service upserts
         * teamio.form_submission_drafts and returns before anything a real submission does).
         *
         * Deliberately NOT the final-submit payload: no Open Dental data, no files list, no
         * grouping block, no overlay, no parent-window message — those belong to the final Submit
         * (_saveFormSubmission) only. A failed save warns and lets the patient keep going.
         * Saves are chained so two quick presses can never land out of order.
         */
        saveDraft({ partIndex } = {}) {
            if (this.isPostOp) return Promise.resolve(); // a post-op after-care page never saves anything
            if (!this.draftEnabled || !this.patNum || this.previewFormID) return Promise.resolve();

            const draftPayload = {
                draft: true,
                siteID: this.siteID,
                formIDForWeb: this.formIDForWeb,
                patNum: this.patNum,
                formUrlID: (this.$route && this.$route.params && this.$route.params.formUrlID) || this.formUrlID || null,
                partIndex: partIndex,
                language: this.currentLanguage,
                formType: this.formType,
                formSubmissionData: JSON.stringify(this.formInputData || {}),
            };

            const run = async () => {
                try {
                    const res = await this.saveFormSubmission(draftPayload);
                    if (!res || res.status !== 200) {
                        throw new Error(`draft save returned ${res && res.status}`);
                    }
                } catch (err) {
                    console.warn('[ViewForm] draft save failed:', err && err.message ? err.message : err);
                    if (this.$toast) this.$toast.warning(t('progressSaveFailed', this.currentLanguage));
                }
            };

            this.draftSaveChain = (this.draftSaveChain || Promise.resolve()).then(run, run);
            return this.draftSaveChain;
        },

        /**
         * Overlay a saved draft onto formSubmissionData. Called once from init(), after the prefill
         * loop. Only keys that still match a control are restored (the form may have been edited
         * since the draft was saved), and never a prefilled control (today's date, the patient's
         * name) so those stay fresh. Then the saved part becomes the renderer's landing part
         * (FormRenderer clamps it to the parts that exist).
         */
        applyPendingDraft() {
            const draft = this.pendingDraft;
            this.pendingDraft = null;
            if (!this.draftEnabled || !draft || !draft.data || typeof draft.data !== 'object') return;

            const controlsByKey = {};
            const controls = (this.formData && this.formData.controls) || {};
            Object.keys(controls).forEach(controlId => {
                const control = controls[controlId];
                if (!control) return;
                controlsByKey[control.name || control.uniqueId || controlId] = control;
            });

            let restored = 0;
            Object.keys(draft.data).forEach(key => {
                const control = controlsByKey[key];
                if (!control) return;            // no such field any more
                if (control.isPrefilled) return; // today's date / patient name stay fresh
                this.formSubmissionData[key] = draft.data[key];
                restored++;
            });

            const idx = Math.floor(Number(draft.partIndex));
            this.draftPartIndex = Number.isFinite(idx) && idx > 0 ? idx : 0;
            this.logToMobile(`Draft restored: ${restored} field(s), landing on part ${this.draftPartIndex + 1}`);
        },

        async _saveFormSubmission() {
            console.log('formInputData', JSON.stringify(this.formInputData, null, 2));
            console.log('user obj', JSON.stringify(this.user, null, 2));
            this.logToMobile('user obj', JSON.stringify(this.user, null, 2));
            this.logToMobile('formInputData', JSON.stringify(this.formInputData, null, 2));

            // Enhanced debug logging: print all field types and values
            console.log('--- Field Type Debug ---');
            Object.keys(this.formInputData).forEach(key => {
                const value = this.formInputData[key];
                console.log(`Field: ${key}, Type: ${Array.isArray(value) ? 'array' : typeof value}, Value:`, value);
            });
            console.log('------------------------');

            // Normalize all array-expected fields based on form config
            Object.keys(this.formData.controls).forEach(key => {
                const control = this.formData.controls[key];
                const controlName = control.name && control.name.trim() !== '' ? control.name : key;
                // List of types that should always be arrays
                const arrayTypes = ['multiSelect', 'checkboxGroup', 'fileUploader', 'allergies', 'diseases', 'medications'];
                if (arrayTypes.includes(control.type)) {
                    if (!Array.isArray(this.formInputData[controlName])) {
                        console.warn(`Normalizing field '${controlName}' to empty array (was:`, this.formInputData[controlName], ')');
                        this.formInputData[controlName] = [];
                    }
                }
            });

            let odDataToSave = {}
            for (const key of Object.keys(this.formData.controls)) {
                let temp = this.formData.controls[key];
                if (temp.teamioODColumn && temp.teamioODColumn !== '') {
                    this.logToMobile('so far temp', temp);
                    this.logToMobile('so far odDataToSave', odDataToSave);

                    if (temp.teamioODColumn === 'Medical' || temp.teamioODColumn === 'Service' || temp.teamioODColumn === 'MedUrgNote') {
                        // Debug log before array operation
                        console.log(`Checking field '${temp.name}' before array operation. Type:`, Array.isArray(this.formInputData[temp.name]) ? 'array' : typeof this.formInputData[temp.name], 'Value:', this.formInputData[temp.name]);
                        if (Array.isArray(this.formInputData[temp.name])) {
                            if (this.formInputData[temp.name].length !== 0) {
                                let valToSave = `${temp.label}: ${this.formInputData[temp.name]}`;
                                odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + valToSave : valToSave;
                            }
                        } else {
                            if (this.formInputData[temp.name] !== '') {
                                let valToSave = `${temp.label}: ${this.formInputData[temp.name]}`;
                                odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + valToSave : valToSave;
                            }
                        }
                    }
                    else if (temp.teamioODColumn === 'Other Allergies' || temp.teamioODColumn === 'Other Problems' || temp.teamioODColumn === 'Other Medications') {
                        let ctrlKey;
                        let currentValue = this.formInputData[temp.name];
                        // Only process if currentValue is a valid non-empty string (exclude 'undefined' literal)
                        if (currentValue && typeof currentValue === 'string' && currentValue.trim() !== '' && currentValue.trim().toLowerCase() !== 'undefined') {
                            this.logToMobile('found other column', temp.name);
                            this.logToMobile('current value', currentValue);

                            switch (temp.teamioODColumn) {
                                case 'Other Allergies':
                                    ctrlKey = this.getControlFromTeamioODColumn('Allergies');
                                    break;

                                case 'Other Problems':
                                    ctrlKey = this.getControlFromTeamioODColumn('Diseases');
                                    break;

                                case 'Other Medications':
                                    ctrlKey = this.getControlFromTeamioODColumn('Medications');
                                    break;

                                default: break;
                            }

                            this.logToMobile('ctrlKey', ctrlKey);
                            let ctrl = this.formData.controls[ctrlKey];
                            let currentSelectedOptions = this.formInputData[ctrl.name];
                            // Debug log before .find
                            console.log(`Checking field '${ctrl.name}' before .find. Type:`, Array.isArray(currentSelectedOptions) ? 'array' : typeof currentSelectedOptions, 'Value:', currentSelectedOptions);
                            // Defensive: ensure currentSelectedOptions is always an array
                            if (!Array.isArray(currentSelectedOptions)) {
                                currentSelectedOptions = [];
                            }
                            this.logToMobile('currentSelectedOptions', JSON.stringify(currentSelectedOptions, null, 2));

                            let otherOption = currentSelectedOptions.find(a => a.text && a.text.toUpperCase() === 'OTHER');
                            if (otherOption) {
                                this.logToMobile('other is already selected');
                                this.logToMobile('otherOption', otherOption);
                                otherOption['otherTypeDescriptionText'] = currentValue;
                            } else if (ctrl && Array.isArray(ctrl.selectedOptions)) {
                                let optionToPush = ctrl.selectedOptions.find(a => a.text && a.text.toUpperCase() === 'OTHER');
                                this.logToMobile('found other option', optionToPush);
                                this.logToMobile('now adding it to', this.formInputData[ctrl.name]);

                                if (optionToPush) {
                                    optionToPush['otherTypeDescriptionText'] = currentValue;
                                    if (!Array.isArray(this.formInputData[ctrl.name])) {
                                        this.formInputData[ctrl.name] = [];
                                    }
                                    this.formInputData[ctrl.name].push(optionToPush);
                                    this.logToMobile('other option added automatically');
                                }
                            }
                            odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + this.formInputData[temp.name] : this.formInputData[temp.name];
                        }
                    }
                    else
                        odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + this.formInputData[temp.name] : this.formInputData[temp.name];
                }
            }

            this.applyCategorizedOtherNote(odDataToSave);

            this.logToMobile('odDataToSave', JSON.stringify(odDataToSave, null, 2));

            const { attachments, error: attachmentError } = collectFormAttachments(this.formData.controls, this.formInputData);
            if (attachmentError) {
                this.$toast.error(attachmentError);
                return false;
            }
            this.logToMobile(`Collected ${attachments.length} attachment(s) from form controls`);

            // Build patient name from available sources (getLongUrl > patientOdData > odDataToSave)
            const _fName = this.patientFName || (this.patientOdData && this.patientOdData.FName) || odDataToSave.FName;
            const _lName = this.patientLName || (this.patientOdData && this.patientOdData.LName) || odDataToSave.LName;
            const _patientName = (_fName && _lName) ? `${_fName} ${_lName}` : (_fName || _lName || null);

            let payload = {
                formName: this.formName,
                formIDForWeb: this.formIDForWeb,
                formSubmissionIDForWeb: this.formSubmissionIDForWeb,
                formSubmissionData: JSON.stringify(this.formInputData),
                formType: this.formType,
                odDataToSave: odDataToSave,
                siteID: this.siteID,
                patNum: this.patNum,
                userODNum: this.user && this.user.ODUserNum ? this.user.ODUserNum : 0,
                patientName: _patientName,
                pdfCategory: this.formData.formConfig.pdfCategory,
                pdfFileName: this.formData.formConfig.pdfFileName,
                attachmentCategory: this.formData.formConfig.attachmentCategory,
                attachments: attachments,
                submittedByPatient: true,
                _date: moment().format('YYYY-MM-DD HH:mm:ss'),
                language: this.currentLanguage
            };

            // Plan-scoped consent completion for Tx Presenter: stamp the plan this form belongs to.
            const _txPlan = parseInt(this.$route.query.txPlan, 10);
            if (Number.isFinite(_txPlan) && _txPlan > 0) {
                payload.treatPlanNum = _txPlan;
            }

            // Action Center: a packet link-open produces ONE card, so tell the backend which packet
            // and run this section belongs to. Only when the link carried a packetID and true
            // positions — anything less (legacy link, garbled numbers) sends no block and the office
            // gets the old per-form card instead of a wrong "3 of 7".
            const _packet = this.packetProgress;
            if (_packet && _packet.packetID && _packet.step && _packet.total) {
                payload.packet = {
                    packetID: _packet.packetID,
                    packetName: _packet.packetName,
                    packetRun: _packet.packetRun,
                    step: _packet.step,
                    total: _packet.total,
                    isLast: _packet.isLast,
                    patientSpecific: _packet.patientSpecific
                };
            }

            this.logToMobile('payload', JSON.stringify(payload, null, 2));

            try {
                // First save submission, then dispatch captured logs to include all console output
                const saveRes = await this.saveFormSubmission(payload);
                await this._sendCapturedLogs();
                this.logToMobile(`Result of saveFormSubmission`, saveRes);
                // ...existing code for handling saveRes...
                if (saveRes && saveRes.status === 200) {
                    let siteInfo = saveRes.data;

                    // Notify a same-origin parent window (Tx Presenter patient-view wizard embeds
                    // this form page in an iframe) that the submission succeeded. Same-origin only
                    // (targetOrigin = window.location.origin); must never break the normal flow.
                    if (window.self !== window.top) {
                        try {
                            window.parent.postMessage({
                                type: 'teamio:form-submitted',
                                formIDForWeb: this.formIDForWeb,
                                formSubmissionIDForWeb: this.formSubmissionIDForWeb,
                            }, window.location.origin);
                        } catch (postErr) {
                            console.warn('teamio:form-submitted postMessage failed', postErr);
                        }
                    }

                    // Check if this form is part of a packet with more forms to show.
                    // nextPacketQuery pops the head of packetNext/packetSteps and rebuilds the
                    // query from an EXPLICIT allowlist — aptNum/codes/txPlan must NOT ride along,
                    // the next form is resolved unscoped.
                    const { nextUrlID, query: nextQuery } = nextPacketQuery(this.$route.query, this.$route.query.lang);
                    if (nextUrlID) {
                        // Resolve the next form's URL via getLongUrl
                        try {
                            const nextRes = await this.$store.dispatch('urlredirect/getLongUrl', { urlID: nextUrlID });
                            if (nextRes && nextRes.status === 200) {
                                const nextData = nextRes.data;

                                this.$router.push({
                                    name: 'ViewFormWithUrlID',
                                    params: {
                                        formUrlID: nextData.formUrlID,
                                        urlID: nextUrlID
                                    },
                                    query: nextQuery
                                });
                                return;
                            }
                        } catch (nextErr) {
                            console.error('Error loading next form in packet:', nextErr);
                            // Fall through to show FormResult
                        }
                    }

                    // No more forms in packet (or not a packet) — show Thank You page
                    this.$router.push({
                        name: 'FormResult',
                        params: {
                            logoUrl: this.formData.formConfig.logoUrl,
                            siteInfo: siteInfo,
                            formConfig: this.formData.formConfig || {}
                        }
                    });
                }
            } catch (error) {
                this.logToMobile(`error in _saveFormSubmission: ${error.message}`);
                this.$toast.error(`Something went wrong. Please try again later`);
            } finally {
                this.busy = false;
            }
        }
    },

    computed: {
        ...mapState({
            loading: state => state.alert.loading,
            loaded: state => state.alert.loaded,
            errored: state => state.alert.errored,
            alertMessage: state => state.alert.message,
            user: state => state.customer.user,
        }),
        isDebugMode() {
            return this.$route.query.debug === '1';
        },
        showLanguageToggle() {
            return this.formData && formSupportsLanguage(this.formData);
        },

        /**
         * Packet state for THIS form, read straight off the route query (see
         * libraries/packet-progress.js for the contract and the fail-soft rules).
         *
         * null means "not part of a packet" — the progress strip is hidden and FormRenderer keeps
         * its normal Submit button. The builder's live preview is never a packet, so it is excluded
         * outright rather than relying on a preview route never carrying the keys.
         */
        packetProgress() {
            if (this.previewFormID) return null;
            const progress = resolvePacketProgress(this.$route.query);
            return progress.isPacket ? progress : null;
        }
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
// Patient-facing Form Page Styling - matching EditForm's modern look
.patient-form-page {
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    color: #334155;
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 2rem;
    // Prevent horizontal overflow on mobile
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
    box-sizing: border-box;
}

// Patient Header with Logo
.patient-form-header {
    background-color: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 20px 2rem;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .header-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 12px;
    }
}

.practice-logo {
    max-height: 80px;
    max-width: 280px;
    object-fit: contain;
}

.form-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    line-height: 1.3;
}

// Post-op after-care link: the practice name + a small "After-care instructions" eyebrow above the form title.
.postop-practice {
    font-size: 0.95rem;
    font-weight: 600;
    color: #475569;
}
.postop-eyebrow {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0f7259;
}
// room for the fixed "I've read these instructions" bar so it never covers the last lines of the instructions
.patient-form-page--postop {
    padding-bottom: 140px;
}

// Language toggle styling in header
.header-language-toggle {
    margin-top: 8px;
}

// Form Container - matching EditForm's full-width layout
.patient-form-container {
    max-width: 100%; // Full width like EditForm
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem 1rem 1rem;
    }
}

// Loading State
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 120px 20px;
    min-height: 300px;
    color: #64748b;
    font-size: 1.1rem;
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    border: none;

    i {
        font-size: 2.5rem;
        color: #3abf94;
    }

    span {
        font-weight: 500;
    }
}

// Debug Toggle Button
.debug-toggle-btn {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 9999;
    padding: 8px 12px;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #2563eb;
    }
}

// Override FormRenderer internal styles for patient-facing view
// Styled to match EditForm's config-card design exactly
::v-deep .vue-form-renderer {
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    border: none;
    overflow: visible;
    padding: 0;
    // Prevent horizontal overflow and ensure text wraps
    max-width: 100%;
    box-sizing: border-box;

    // Global text wrapping for all content
    h1, h2, h3, h4, h5, h6, p, span, div, li, td, th, label {
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-word;
        max-width: 100%;
    }

    // Form headline container
    .form-headline-container {
        text-align: center;
        padding: 0 16px;
        margin-bottom: 24px;
        max-width: 100%;
        box-sizing: border-box;

        h1 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #0f172a;
            margin: 0 0 8px 0;
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
            hyphens: auto;
        }

        p {
            font-size: 0.9375rem;
            color: #64748b;
            margin: 0;
            line-height: 1.5;
        }
    }

    // Section container - the outer card wrapper (matches .config-card)
    .section-container {
        background-color: white;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 2px solid #e2e8f0;
        margin-bottom: 24px;
        overflow: visible; // Allow dropdowns to overflow
        transition: all 0.2s ease-in-out;
        max-width: 100%;
        box-sizing: border-box;

        &:last-child {
            margin-bottom: 0;
        }

        // Hover effect - green border and subtle green tint
        &:hover {
            border-color: #3abf94;
            box-shadow: 0 2px 8px rgba(58, 191, 148, 0.15);
        }

        // When section has focus-within (a field inside is focused)
        &:focus-within {
            border-color: #3abf94;
            box-shadow: 0 2px 8px rgba(58, 191, 148, 0.15);
        }
    }

    // Normal section - transparent, let container handle the card look
    // Override the global v-form-builder.css padding styles
    .normal-section,
    .renderer-section {
        padding: 0 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        margin: 0 !important;
        background-color: transparent !important;
        border-radius: 0 !important;
        border: none !important;
    }

    // Override page break styling (for PDF)
    .html2pdf__page-break {
        display: none !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
    }

    // Section headline styling - gray header bar (matches .config-card-header exactly)
    .headline-block,
    .section-headline {
        padding: 16px 20px !important;
        margin: 0 !important;
        background-color: #f8fafc !important;
        border-bottom: 1px solid #e2e8f0 !important;
        border-radius: 12px 12px 0 0 !important; // Top corners rounded
        user-select: auto;

        h2,
        .section-title {
            font-size: 1rem !important;
            font-weight: 600 !important;
            color: #0f172a !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            display: flex !important;
            align-items: center;
            gap: 10px;
            line-height: 1.4;
        }

        p,
        .section-subtitle {
            font-size: 0.8125rem !important;
            color: #64748b !important;
            margin: 4px 0 0 0 !important;
            padding: 0 !important;
            line-height: 1.4;
            font-weight: 400;
        }
    }

    // Section controls container - the body (matches .config-card-body exactly)
    .section-controls,
    .control-list-container {
        padding: 20px !important;
        margin: 0 !important;
        display: flex;
        flex-wrap: wrap;
        gap: 0;
        background-color: white !important;
        border-radius: 0 0 12px 12px !important; // Bottom corners rounded
    }

    // Row class for flex layout
    .row {
        display: flex;
        flex-wrap: wrap;
        margin: 0 -8px;
        width: calc(100% + 16px);
    }

    // Control wrapper styling - handle Bootstrap col classes for side-by-side
    .control-view-wrapper {
        margin-bottom: 16px;
        padding: 0 8px;
        box-sizing: border-box;

        &:last-child {
            margin-bottom: 0;
        }

        // Fix for side-by-side controls (Bootstrap col classes)
        &[class*="col-"] {
            padding-left: 8px;
            padding-right: 8px;
        }
    }

    // Control view styling with hover effect
    .control-view {
        padding: 12px;
        border: 1px solid transparent;
        border-radius: 8px;
        background-color: transparent;
        transition: all 0.15s ease-in-out;

        // Hover effect - gray rounded background
        &:hover {
            background-color: #f8fafc;
            border-color: #e2e8f0;
        }

        // Focus-within effect - when a field inside is focused
        &:focus-within {
            background-color: #f1f5f9;
            border-color: #e2e8f0;
        }
    }

    // Label styling - matches sidebar labels exactly
    .control-label-container {
        margin-bottom: 6px;
        padding-bottom: 0;
        line-height: 1.4;

        label {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #475569;
            margin-bottom: 0;
            display: block;
        }

        .text-danger {
            color: #ef4444;
            margin-left: 2px;
        }
    }

    // Form control styling - matches sidebar inputs with light gray borders
    // Using !important to override Bootstrap defaults
    .form-control,
    input.form-control,
    textarea.form-control,
    select.form-control {
        padding: 10px 14px !important;
        border: 1px solid #e2e8f0 !important;
        border-radius: 8px !important;
        // 16px at EVERY width, never rem (the app root is 14px): iOS Safari zooms in when a field
        // under 16px is tapped and never zooms back out. Pinned by ios-focus-zoom-guardrail.test.js.
        font-size: 16px !important;
        color: #334155 !important;
        background-color: white !important;
        transition: all 0.2s ease !important;
        width: 100% !important;
        box-sizing: border-box !important;
        line-height: 1.5 !important;
        font-family: inherit !important;
        box-shadow: none !important;

        &:hover {
            border-color: #cbd5e1 !important;
        }

        &:focus {
            outline: none !important;
            border-color: #3abf94 !important;
            box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1) !important;
        }

        &::placeholder {
            color: #94a3b8 !important;
        }
    }

    textarea.form-control {
        min-height: 80px !important;
        resize: vertical !important;
    }

    select.form-control {
        appearance: none !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
        background-position: right 10px center !important;
        background-repeat: no-repeat !important;
        background-size: 16px !important;
        padding-right: 36px !important;
        cursor: pointer !important;
    }

    // Also style any input/textarea/select that might not have .form-control
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="number"],
    input[type="date"],
    input[type="time"],
    input[type="datetime-local"],
    input[type="password"],
    input[type="url"],
    textarea,
    select {
        padding: 10px 14px !important;
        border: 1px solid #e2e8f0 !important;
        border-radius: 8px !important;
        font-size: 16px !important; // iOS focus-zoom floor, see .form-control above
        color: #334155 !important;
        background-color: white !important;
        box-shadow: none !important;

        &:hover {
            border-color: #cbd5e1 !important;
        }

        &:focus {
            outline: none !important;
            border-color: #3abf94 !important;
            box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1) !important;
        }
    }

    // Vue-Multiselect styling for Diseases/Allergies/Medications dropdowns
    .multiselect {
        border: 1px solid #e2e8f0 !important;
        border-radius: 8px !important;
        min-height: 42px !important;
        background-color: white !important;
        box-shadow: none !important;
        position: relative !important;
        z-index: 1 !important;

        &:hover {
            border-color: #cbd5e1 !important;
        }

        // When dropdown is active/open, increase z-index
        &--active {
            z-index: 100 !important;
        }

        .multiselect__tags {
            border: none !important;
            background-color: transparent !important;
            padding: 6px 40px 6px 10px !important;
            min-height: 40px !important;
            border-radius: 8px !important;
        }

        .multiselect__select {
            width: 40px !important;
            height: 100% !important;
            background-color: transparent !important;
            border: none !important;
            right: 0 !important;
            top: 0 !important;
            padding: 0 !important;

            &::before {
                border-color: #6b7280 transparent transparent transparent !important;
                border-width: 5px 5px 0 5px !important;
                top: 50% !important;
                margin-top: -2px !important;
            }
        }

        .multiselect__tag {
            background-color: #3abf94 !important;
            color: white !important;
            border-radius: 4px !important;
            padding: 4px 26px 4px 8px !important;
            margin: 2px 4px 2px 0 !important;
            font-size: 0.8125rem !important;
        }

        .multiselect__tag-icon {
            &::after {
                color: white !important;
            }
            &:hover {
                background-color: #2fa37f !important;
            }
        }

        // The search box is a real <input>, so it needs the 16px iOS focus-zoom floor; the placeholder
        // and the picked value match it so the text does not jump size when the box opens.
        .multiselect__placeholder {
            color: #94a3b8 !important;
            font-size: 16px !important;
            padding: 0 !important;
            margin: 0 !important;
        }

        .multiselect__single {
            font-size: 16px !important;
        }

        .multiselect__input {
            font-size: 16px !important;
            color: #334155 !important;
            background-color: transparent !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
        }

        .multiselect__content-wrapper {
            border: 1px solid #e2e8f0 !important;
            border-radius: 8px !important;
            margin-top: 4px !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
            z-index: 1000 !important;
            position: absolute !important;
            background-color: white !important;
            max-height: 300px !important;
            overflow-y: auto !important;
        }

        .multiselect__content {
            display: block !important;
        }

        .multiselect__element {
            display: block !important;
        }

        .multiselect__option {
            padding: 10px 14px !important;
            font-size: 0.875rem !important;
            color: #334155 !important;

            &--highlight {
                background-color: #3abf94 !important;
                color: white !important;
            }

            &--selected {
                background-color: #f0fdf4 !important;
                color: #166534 !important;
                font-weight: 500 !important;
            }
        }
    }

    // Checkbox and radio styling - simple like sidebar
    .radio-checkbox {
        padding: 4px 0;

        label {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 0;
            margin: 0;
            background-color: transparent;
            border: none;
            border-radius: 0;
            cursor: pointer;
            transition: all 0.15s ease;
            font-size: 0.875rem;
            color: #334155;
            font-weight: 500;

            &:hover {
                color: #1e293b;
            }

            input[type="checkbox"],
            input[type="radio"] {
                width: 18px;
                height: 18px;
                accent-color: #3abf94;
                cursor: pointer;
                flex-shrink: 0;
            }
        }

        &.next label {
            display: inline-flex;
            margin-right: 16px;
        }
    }

    // Signature field styling - wider and more prominent
    .signature-container {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 16px;
        background-color: #f8fafc;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;

        .signature-instructions {
            font-size: 0.875rem !important;
            color: #64748b !important;
            margin-bottom: 12px !important;
            text-align: center !important;
        }

        .signature-wrapper {
            border: 2px dashed #cbd5e1 !important;
            border-radius: 8px !important;
            background-color: white !important;
            min-height: 200px !important;
            width: 100% !important;

            &:hover {
                border-color: #3abf94 !important;
            }
        }

        .signature-pad {
            width: 100% !important;
            min-height: 200px !important;
            height: 200px !important;
        }

        canvas {
            border-radius: 6px;
            background-color: white;
            border: none !important;
            width: 100% !important;
        }

        #clear-signature {
            margin-top: 12px !important;
            padding: 8px 16px !important;
            background-color: #f1f5f9 !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 6px !important;
            color: #64748b !important;
            font-size: 0.8125rem !important;
            font-weight: 500 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            text-align: center !important;

            &:hover {
                background-color: #e2e8f0 !important;
                color: #334155 !important;
            }
        }
    }

    // File upload styling - comprehensive styling for FileUploaderControl
    .example-full {
        // Remove any default borders/backgrounds from wrapper divs
        > div,
        > .b-overlay-wrap > div {
            border: none !important;
            background: transparent !important;
        }

        // File attachments list header
        h3.mt-3 {
            font-size: 1rem !important;
            font-weight: 600 !important;
            color: #334155 !important;
            margin-bottom: 12px !important;
            margin-top: 0 !important;
        }

        // File attachments list
        ul {
            list-style: none !important;
            padding: 0 !important;
            margin: 0 0 16px 0 !important;

            li {
                display: flex !important;
                align-items: center !important;
                gap: 10px !important;
                padding: 10px 14px !important;
                background-color: #f8fafc !important;
                border: 1px solid #e2e8f0 !important;
                border-radius: 8px !important;
                margin-bottom: 8px !important;

                img {
                    width: 32px !important;
                    height: 32px !important;
                    border-radius: 4px !important;
                    object-fit: cover !important;
                }

                a {
                    color: #3abf94 !important;
                    text-decoration: none !important;
                    font-size: 0.875rem !important;
                    font-weight: 500 !important;
                    flex: 1 !important;

                    &:hover {
                        text-decoration: underline !important;
                    }
                }

                .fa-trash {
                    color: #94a3b8 !important;
                    cursor: pointer !important;
                    padding: 4px !important;
                    transition: color 0.2s ease !important;

                    &:hover {
                        color: #ef4444 !important;
                    }
                }

                .fa-spinner {
                    color: #3abf94 !important;
                }
            }
        }

        // No attachments text
        > div > span {
            color: #64748b !important;
            font-size: 0.875rem !important;
            font-style: italic !important;
        }

        // Upload area - hide the table completely and restyle
        .upload {
            .table-responsive {
                border: none !important;
                overflow: visible !important;
            }

            table.table,
            .table {
                border: none !important;
                margin-bottom: 0 !important;
                border-collapse: collapse !important;

                // Hide table header completely
                thead,
                thead tr,
                thead th {
                    display: none !important;
                    visibility: hidden !important;
                    height: 0 !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    border: none !important;
                }

                tbody {
                    display: block !important;

                    tr {
                        display: block !important;
                        border: none !important;
                        background: transparent !important;

                        td {
                            display: block !important;
                            border: none !important;
                            padding: 0 !important;
                            background: transparent !important;

                            // Drop zone styling
                            .text-center.p-5 {
                                border: 2px dashed #e2e8f0 !important;
                                border-radius: 12px !important;
                                padding: 40px 20px !important;
                                background-color: #f8fafc !important;
                                transition: all 0.2s ease !important;

                                &:hover {
                                    border-color: #3abf94 !important;
                                    background-color: rgba(58, 191, 148, 0.04) !important;
                                }

                                h4 {
                                    font-size: 1rem !important;
                                    font-weight: 500 !important;
                                    color: #64748b !important;
                                    margin-bottom: 16px !important;
                                }

                                // "Select Files" button inside drop zone
                                label.btn,
                                label.btn-lg,
                                label.btn-primary {
                                    display: inline-flex !important;
                                    align-items: center !important;
                                    justify-content: center !important;
                                    gap: 8px !important;
                                    padding: 12px 24px !important;
                                    background-color: #3abf94 !important;
                                    color: white !important;
                                    border: none !important;
                                    border-radius: 8px !important;
                                    font-size: 0.875rem !important;
                                    font-weight: 600 !important;
                                    cursor: pointer !important;
                                    transition: all 0.2s ease !important;

                                    &:hover {
                                        background-color: #2fa37f !important;
                                        transform: translateY(-1px) !important;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Footer with Select button - HIDE it since we already have Select Files in drop zone
        .example-foorer {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
        }

        // Hide any empty btn-group divs that might be rendering inside the file uploader
        > .b-overlay-wrap .btn-group:empty,
        > .b-overlay-wrap .dropdown-menu:empty,
        .upload .btn-group:empty,
        .upload .dropdown-menu:empty {
            display: none !important;
        }

        // Hide the action dropdown toggle in the file list table only
        .upload table .btn-group,
        .upload table .dropdown-toggle,
        .upload table .dropdown-menu,
        .example-foorer .btn-group {
            display: none !important;
        }

        // Ensure the wrapper div around the upload doesn't have weird styling
        > .b-overlay-wrap > div > div:last-child {
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
        }

        // Drop active overlay
        .drop-active {
            background: rgba(58, 191, 148, 0.9) !important;

            h3 {
                color: white !important;
                font-size: 1.5rem !important;
            }
        }
    }

    // Legacy file-uploader class (in case it's used elsewhere)
    .file-uploader {
        border: 2px dashed #e2e8f0;
        border-radius: 12px;
        padding: 40px 20px;
        text-align: center;
        background-color: #f8fafc;
        transition: all 0.2s ease;

        &:hover {
            border-color: #3abf94;
            background-color: rgba(58, 191, 148, 0.04);
        }
    }

    // Date picker styling
    .date-input-wrapper {
        position: relative;

        input {
            padding-right: 36px;
        }

        .calendar-icon {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #6b7280;
        }
    }

    // Submit button container - card style matching EditForm
    .submit-button-container {
        margin: 24px 0 0 0;
        padding: 20px;
        background-color: white;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        text-align: center;
    }

    // Submit button styling - green like sidebar primary buttons
    .submit-button,
    .btn-success,
    button[type="submit"] {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 10px 18px;
        background-color: #3abf94;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 160px;
        text-transform: none;

        &:hover:not(:disabled) {
            background-color: #2fa37f;
            transform: translateY(-1px);
        }

        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
        }

        .fa-spinner {
            animation: spin 1s linear infinite;
        }
    }

    // Required field indicator
    .required-indicator,
    .text-danger {
        color: #ef4444;
        font-weight: 500;
    }

    // Error message styling
    .invalid-feedback,
    .error-message {
        color: #ef4444;
        font-size: 0.75rem;
        margin-top: 4px;
    }

    // Validation error outline
    .is-invalid {
        border-color: #ef4444 !important;

        &:focus {
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
    }
}

// Animation for spinner
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

// Mobile responsiveness for tablets (768px and below)
@media (max-width: 768px) {
    // Ensure page doesn't overflow horizontally
    .patient-form-page {
        overflow-x: hidden;
        width: 100%;
        max-width: 100vw;
    }

    .patient-form-header {
        padding: 16px;
        margin-bottom: 12px;
    }

    .practice-logo {
        max-height: 60px;
        max-width: 200px;
    }

    .form-title {
        font-size: 1.25rem;
    }

    .patient-form-container {
        padding: 0 12px 12px 12px;
        max-width: 100%;
        box-sizing: border-box;
    }

    // Card sections - reduce padding on mobile
    ::v-deep .vue-form-renderer {
        max-width: 100%;
        overflow-x: hidden;

        .section-container {
            margin-bottom: 12px;
            border-radius: 10px;
            max-width: 100%;
            box-sizing: border-box;

            // Disable hover effects on touch devices - no visual change on hover
            &:hover {
                border-color: #e2e8f0;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
            }
        }

        .headline-block,
        .section-headline {
            padding: 12px 14px !important;
            border-radius: 10px 10px 0 0 !important;

            h2,
            .section-title {
                font-size: 0.9375rem !important;
            }
        }

        .section-controls,
        .control-list-container {
            padding: 12px !important;
            border-radius: 0 0 10px 10px !important;
        }

        // Disable hover effect on controls for touch - cleaner look
        .control-view {
            padding: 8px !important;
            margin-bottom: 4px;

            &:hover,
            &:focus-within {
                background-color: transparent !important;
                border-color: transparent !important;
            }
        }

        // Reduce spacing between fields
        .control-view-wrapper {
            margin-bottom: 8px !important;
            padding: 0 4px !important;
        }

        // Make form controls more touch-friendly
        .form-control,
        input.form-control,
        textarea.form-control,
        select.form-control,
        input[type="text"],
        input[type="email"],
        input[type="tel"],
        input[type="number"],
        input[type="date"] {
            // The base rule already sets 16px at every width (iOS focus-zoom); repeated here only so
            // this touch block is self-contained. It must never go below 16px.
            font-size: 16px !important;
            padding: 12px 14px !important;
            min-height: 44px !important; // Touch-friendly target size
            box-sizing: border-box !important;
            width: 100% !important;
        }

        // Labels - more compact
        .control-label-container {
            margin-bottom: 4px !important;

            label {
                font-size: 0.875rem !important;
                font-weight: 600 !important;
            }
        }

        // Multiselect - larger touch targets
        .multiselect {
            min-height: 44px !important;
            max-width: 100% !important;

            .multiselect__tags {
                min-height: 42px !important;
                padding: 6px 40px 6px 10px !important;
            }

            .multiselect__option {
                padding: 12px 14px !important;
            }
        }

        // Checkboxes and radios - larger touch targets
        .radio-checkbox label {
            padding: 8px 0 !important;
            min-height: 40px;

            input[type="checkbox"],
            input[type="radio"] {
                width: 20px !important;
                height: 20px !important;
            }
        }

        // Submit button - full width on mobile
        .submit-button-container {
            padding: 12px !important;
            margin-top: 12px !important;
        }

        .submit-button,
        .btn-success,
        button[type="submit"] {
            width: 100% !important;
            padding: 14px 20px !important;
            min-height: 50px !important;
            font-size: 1rem !important;
        }

        // Signature - ensure it's usable on touch devices
        .signature-container {
            padding: 10px;

            .signature-pad,
            .signature-wrapper {
                min-height: 150px !important;
            }
        }

        // File uploader - larger drop zone
        .example-full .upload .text-center.p-5 {
            padding: 24px 12px !important;
        }
    }
}

// Mobile responsiveness for phones (480px and below)
@media (max-width: 480px) {
    .patient-form-page {
        overflow-x: hidden;
        width: 100%;
        max-width: 100vw;
    }

    .patient-form-header {
        padding: 10px 12px;
        margin-bottom: 8px;

        .header-content {
            gap: 6px;
        }
    }

    .practice-logo {
        max-height: 45px;
        max-width: 140px;
    }

    .form-title {
        font-size: 1rem;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }

    .patient-form-container {
        padding: 0 8px 8px 8px;
        max-width: 100%;
        box-sizing: border-box;
    }

    ::v-deep .vue-form-renderer {
        max-width: 100%;

        // Form headline - smaller on phones
        .form-headline-container {
            padding: 0 8px;
            margin-bottom: 16px;

            h1 {
                font-size: 1.125rem !important;
                line-height: 1.3;
            }

            p {
                font-size: 0.8125rem !important;
            }
        }

        .section-container {
            margin-bottom: 8px;
            border-radius: 8px;
            border-width: 1px;
            max-width: 100%;
        }

        .headline-block,
        .section-headline {
            padding: 10px 12px !important;
            border-radius: 8px 8px 0 0 !important;

            h2,
            .section-title {
                font-size: 0.875rem !important;
                word-wrap: break-word !important;
                overflow-wrap: break-word !important;
            }

            p,
            .section-subtitle {
                font-size: 0.75rem !important;
                margin-top: 2px !important;
            }
        }

        .section-controls,
        .control-list-container {
            padding: 10px !important;
            border-radius: 0 0 8px 8px !important;
        }

        // Even more compact on small phones
        .control-view {
            padding: 6px !important;
            max-width: 100%;
        }

        .control-view-wrapper {
            margin-bottom: 6px !important;
            padding: 0 2px !important;
            max-width: 100%;
        }

        // Labels - slightly smaller on phones
        .control-label-container {
            margin-bottom: 3px !important;

            label {
                font-size: 0.8125rem !important;
            }
        }

        // Smaller inputs on very small screens
        .form-control,
        input.form-control {
            padding: 10px 12px !important;
            min-height: 42px !important;
        }

        // Ensure all text content wraps properly
        h1, h2, h3, h4, h5, h6, p, li, td, th {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            max-width: 100% !important;
            hyphens: auto;
        }
    }
}
</style>

<style src="./formstyles.scss" lang="scss" />
